// Module-scope registry of guarded flows the user has already exited.
//
// Why this exists. A guarded flow does not occupy one history entry, it occupies several.
// usePreventBrowserBackButton pushes a duplicate href on mount
// (hooks/usePreventBrowserBackButton.jsx:186), and some flows push more of their own:
// /tran via
// components/FormAddTransaction/components/CustomReceiversDropdown/ver2/index.jsx:150 ->
// utils/func.js:294, and /register via components/FormRegister/LogInDetails/index.jsx:137.
// The /tran count is path-dependent, not fixed: each further receiver selection pushes
// another entry, and applying a discount from inside the flow pushes one more
// (components/Discount/components/DiscountTicket/index.jsx:135).
// So the obvious fix -- rewriting the top entry on the way out with
// navigate(dest, { replace: true }) -- only overwrites the last of those entries and
// leaves the earlier ones below. One Back press lands on one of them and the user is
// back inside the flow they just left. That approach is deliberately NOT taken anywhere.
//
// This module takes the other side of the problem: rather than trying to rewrite
// departure, it records the departure and validates arrival. The guard consults it on
// mount, and a mount that is a Back-press re-entry into an exited flow is bounced to the
// flow's home instead of being armed again.
//
// Deliberately not a hook and deliberately module scope, mirroring helpers/reloadGate.js:
// the guard needs this fact during its own layout effect, before any React context or
// state could carry it, and the two helpers are siblings owned by the same guard.

// Exact pathname strings only. NEVER prefix matching.
//
// Three guarded flows live under the same /receivers/ prefix --
// /receivers/add-receiver, /receivers/update-receiver/:receiverId and
// /receivers/edit-receiver/:receiverId (constants/router/index.js:11-13,
// router/index.js:166-176). A prefix or `includes` match would retire all three the
// moment the user left any one of them, so leaving add-receiver would lock the user out
// of edit-receiver.
//
// components/common/Layout/index.jsx uses exactly that prefix idiom for its
// pathsHideFooter / pathsHideHeader lists (`pathname.includes(path)`, e.g. :91, :95).
// That is fine there -- hiding a footer on a near-miss is cosmetic. Copying the idiom
// here would be the bug. A Set keyed on the full pathname is the whole point.
//
// Keying on the full pathname also means /receivers/edit-receiver/A and
// /receivers/edit-receiver/B are tracked separately, which is what we want: exiting the
// edit flow for one receiver says nothing about the other.
const retiredFlows = new Set();

// Module scope is load-bearing, and this must NEVER be persisted.
//
// A full document load has to reset this registry, because a whole-document re-entry
// into a guarded flow is a legitimate, expected event: the payment return.
// pages/AddTransactionPage/ver2/index.jsx:133-134 build
// `${homeUrl}/tran?refNumber=...&paymentStatus=...` and assign it to
// window.location.href at :151, :153 and :164; hooks/fx/useFxPayment.jsx:425-431 do the
// same for /fx-tran. The user left /tran (retiring it), went to the payment provider,
// and the provider sends them back to /tran as a brand-new document.
//
// Because the registry lives only in module memory, that new document starts with an
// empty Set and the return is simply a normal arrival. If this were moved to
// sessionStorage or localStorage -- which looks like a robustness improvement -- the
// retirement would survive the document swap, the returning /tran?paymentStatus=SUCCESS
// load would be classified as a re-entry into a retired flow, and the user would be
// redirected to /transactions instead of seeing their payment result. Do not persist it.

// Record that the guarded flow at this exact pathname has been exited.
export const retireFlow = (pathname) => {
  // Ignore rather than store a falsy key. A null/undefined/'' entry can never be matched
  // by a real pathname, so storing it would only leave a permanent junk member in the Set
  // and make the test seam's view of the state misleading.
  if (!pathname) return;

  retiredFlows.add(pathname);
};

// Exact-match lookup. See the retiredFlows comment for why this is not a prefix test.
export const isFlowRetired = (pathname) => {
  if (!pathname) return false;

  return retiredFlows.has(pathname);
};

// Clear one entry. Called by the guard on a legitimate arrival, so the flow is armed
// again, and also immediately after a re-entry redirect so the bounce happens once
// rather than latching.
export const unretireFlow = (pathname) => {
  if (!pathname) return;

  retiredFlows.delete(pathname);
};

// Test seams. Not for production callers.
export const __getFlowReentryGateState = () => ({
  retiredFlows: Array.from(retiredFlows),
});

export const __resetFlowReentryGate = () => {
  retiredFlows.clear();
};
